import { stripHtml } from 'src/utils/stripHtml';
import { formatSnakeCaseStatus } from 'src/components/DynamicCriteriaGenerator/components/CRMStatus/config';
import { isSocSuppressedFilterType } from 'src/components/FilterSets/SiteOfCare/displayHelpers';

/**
 * Get display label from ui_metadata or fallback to type + ID
 *
 * Supports two formats (in order of precedence):
 * 1. NEW: ui_metadata from API response (embedded metadata)
 * 2. FALLBACK: Plain IDs with type-based display
 */
export function getCriteriaDisplayLabel(
  criteriaInput: any,
  componentId: string
): string {
  // Check for ui_metadata from API response - use as primary source
  if (
    criteriaInput.ui_metadata &&
    Array.isArray(criteriaInput.ui_metadata) &&
    criteriaInput.ui_metadata.length > 0
  ) {
    // Special handling for CRM statuses which have a unique structure
    if (criteriaInput.type === 'crm_status') {
      const displays = criteriaInput.ui_metadata.map((status: any) => {
        if (status.displayLabel) {
          return stripHtml(status.displayLabel);
        }
        if (status.crm_status) {
          return stripHtml(formatSnakeCaseStatus(status.crm_status));
        }
        return 'CRM Status';
      });
      if (displays.length > 0) {
        return displays.join(', ');
      }
    }

    // Generic extraction - try common label fields in order of preference
    const labels = criteriaInput.ui_metadata
      .map((item: any) =>
        stripHtml(
          item.label ||
            item.displayLabel ||
            item.name ||
            item.code_name ||
            item.id ||
            ''
        )
      )
      .filter(Boolean);

    if (labels.length > 0) {
      return labels.join(', ');
    }
  }

  // Fallback - show type with ID
  const type = criteriaInput?.type || 'unknown';

  // Special cases for static types
  if (type === 'this_provider') {
    return criteriaInput?.ui_key === 'this_site' ? 'this site' : 'this provider';
  }
  if (type === 'this_site') {
    return 'this site';
  }

  // Special case for entity_type (singleton with value field)
  if (type === 'entity_type') {
    const value = criteriaInput?.value;
    if (value === 'providers') return 'Providers';
    if (value === 'sites') return 'Sites';
    return value || 'Entity Type';
  }

  // Special case for entity_attribute (singleton with value field)
  if (type === 'entity_attribute') {
    const value = criteriaInput?.value;
    // ui_metadata may contain the label; otherwise fall back to the raw value
    if (criteriaInput.ui_metadata?.[0]?.displayLabel) {
      return criteriaInput.ui_metadata[0].displayLabel;
    }
    if (criteriaInput.ui_metadata?.[0]?.label) {
      return criteriaInput.ui_metadata[0].label;
    }
    return value || 'Site of Care Attribute';
  }

  // Extract the ID value from the input
  const id = criteriaInput.value || componentId;

  return `${type}: ${Array.isArray(id) ? id.join(', ') : id}`;
}

/**
 * Gets the appropriate CSS classes for different component types
 * Uses the same color scheme as CodeWithTooltip for consistency
 */
export const getComponentTypeClasses = (componentType?: string, uiKey?: string): string => {
  // Handle ui_key override for this_provider
  if (componentType === 'this_provider' && uiKey === 'this_site') {
    return 'text-teal-600'; // Use teal color for "this site"
  }

  switch (componentType) {
    case 'codes':
      return 'text-sky-600'; // CPT codes - matches getCodeColor
    case 'code_set':
      return 'text-pink-600'; // Code sets
    case 'filter_set':
      return 'text-pink-600'; // Filter sets (same as code sets)
    case 'providers':
      return 'text-purple-600';
    case 'this_provider':
      return 'text-purple-600'; // This provider - purple to match providers
    case 'this_site':
      return 'text-teal-600'; // This site - teal color
    case 'payer_type':
    case 'payer_name':
      return 'text-orange-600';
    case 'territory':
      return 'text-emerald-600';
    case 'list':
    case 'provider_list':
      return 'text-indigo-600';
    case 'crm_status':
      return 'text-teal-600';
    case 'open_payments':
      return 'text-amber-600';
    case 'cohort_id':
    case 'provider_cohort':
      return 'text-cyan-600';
    case 'entity_type':
      return 'text-cyan-600';
    case 'entity_attribute':
      return 'text-rose-600';
    default:
      return 'text-gray-600';
  }
};

/**
 * Replace component placeholders in template with display labels
 */
export function formatCriteriaTemplate(
  template: string,
  criteriaInputs: Record<string, any>
): string {
  return template.replace(/\{\{\s*([^}]+)\s*\}\}/g, (match, componentId) => {
    const trimmedId = componentId.trim();
    const criteriaInput = criteriaInputs[trimmedId];

    if (!criteriaInput) return match;

    return getCriteriaDisplayLabel(criteriaInput, trimmedId);
  });
}

/**
 * Format criteria template with rich information for hoverable display
 * Returns an array of segments with display labels and metadata
 */
export interface CriteriaSegment {
  text: string;
  isComponent: boolean;
  componentId?: string;
  componentType?: string;
  uiKey?: string;
  metadata?: any;
  tooltip?: string | TooltipContent;
}

export function formatCriteriaTemplateWithMetadata(
  template: string,
  criteriaInputs: Record<string, any>
): CriteriaSegment[] {
  const segments: CriteriaSegment[] = [];
  const componentRegex = /\{\{\s*([^}]+)\s*\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = componentRegex.exec(template)) !== null) {
    // Add text before the component
    if (match.index > lastIndex) {
      segments.push({
        text: template.substring(lastIndex, match.index),
        isComponent: false,
      });
    }

    // Add the component
    const componentId = match[1].trim();
    const criteriaInput = criteriaInputs[componentId];

    if (criteriaInput) {
      const displayLabel = getCriteriaDisplayLabel(criteriaInput, componentId);

      segments.push({
        text: displayLabel,
        isComponent: true,
        componentId: componentId,
        componentType: criteriaInput.type,
        uiKey: criteriaInput.ui_key,
        tooltip: getTooltipForComponent(criteriaInput),
      });
    } else {
      // Fallback if component not found
      segments.push({
        text: match[0],
        isComponent: false,
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text
  if (lastIndex < template.length) {
    segments.push({
      text: template.substring(lastIndex),
      isComponent: false,
    });
  }

  return segments;
}

export interface TooltipContent {
  title: string | null;
  sections: string[];
}

/**
 * Generate tooltip content for code_set type from ui_metadata
 * @param codeSetData - The code set data with detailed_filters
 * @returns Structured tooltip data with title and sections
 */
function generateCodeSetTooltip(codeSetData: any): TooltipContent {
  if (!codeSetData) {
    return { title: 'Code Set', sections: [] };
  }

  const result: TooltipContent = {
    title: codeSetData.label
      ? stripHtml(codeSetData.label)
      : codeSetData.name
      ? stripHtml(codeSetData.name)
      : null,
    sections: [],
  };

  // Add detailed breakdown from detailed_filters (same structure as before)
  if (codeSetData.detailed_filters) {
    codeSetData.detailed_filters.forEach((filter: any) => {
      if (filter.detailed_values && filter.detailed_values.length > 0) {
        const codes = filter.detailed_values
          .map((item: any) => item.label || item.value)
          .join(', ');
        result.sections.push(`${filter.type}: ${codes}`);
      }
    });
  }

  return result;
}

/**
 * Generate tooltip content for filter_set type from ui_metadata
 * @param filterSetData - The filter set data with detailed_filters
 * @returns Structured tooltip data with title and sections
 */
function generateFilterSetTooltip(filterSetData: any): TooltipContent {
  if (!filterSetData) {
    return { title: 'Filter Set', sections: [] };
  }

  const result: TooltipContent = {
    title: filterSetData.label
      ? stripHtml(filterSetData.label)
      : filterSetData.name
      ? stripHtml(filterSetData.name)
      : null,
    sections: [],
  };

  // Add detailed breakdown from detailed_filters (same structure as before)
  if (filterSetData.detailed_filters) {
    filterSetData.detailed_filters.forEach((filter: any) => {
      // Hide legacy Place of Service filters so the preview matches what actually
      // gets queried (see useFilterSets strip rule).
      if (isSocSuppressedFilterType(filter.type)) return;
      if (filter.detailed_values && filter.detailed_values.length > 0) {
        // Convert PAYER_TYPE to "Payer Type" format
        const filterLabel = filter.type
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (l: string) => l.toUpperCase());

        const values = filter.detailed_values
          .map((item: any) => item.label || item.value)
          .join(', ');

        result.sections.push(`${filterLabel}: ${values}`);
      }
    });
  }

  return result;
}

/**
 * Get tooltip content for a component
 * @param criteriaInput - The criteria input object containing type and ui_metadata
 * @returns Tooltip content as string or structured object, undefined if no tooltip available
 */
function getTooltipForComponent(
  criteriaInput: any
): string | TooltipContent | undefined {
  const type = criteriaInput?.type as string;

  if (!criteriaInput.ui_metadata || !Array.isArray(criteriaInput.ui_metadata))
    return undefined;

  switch (type) {
    case 'codes':
      // For codes, return description from first code if available
      if (criteriaInput.ui_metadata.length > 0) {
        const firstCode = criteriaInput.ui_metadata[0];
        return firstCode.description || undefined;
      }
      return undefined;
    case 'code_set':
      // Generate tooltip from code set ui_metadata
      if (criteriaInput.ui_metadata[0]) {
        return generateCodeSetTooltip(criteriaInput.ui_metadata[0]);
      }
      return undefined;
    case 'filter_set':
      // Generate tooltip from filter set ui_metadata
      if (criteriaInput.ui_metadata[0]) {
        return generateFilterSetTooltip(criteriaInput.ui_metadata[0]);
      }
      return undefined;
    default:
      // No tooltips for other component types
      return undefined;
  }
}

/**
 * Query plan params that hold entity ids, mapped to the criteria input type that resolves them.
 * Mirrors the backend's QUERY_PLAN_PARAM_INPUT_TYPES, which builds each step's `ui_metadata`.
 */
const QUERY_PLAN_PARAM_INPUT_TYPES: Record<string, string> = {
  code_set: 'code_set',
  filter_set: 'filter_set',
  site_filter_set: 'filter_set',
  site_code_set: 'code_set',
  cohort_id: 'provider_cohort',
  list_ids: 'provider_list',
  institution_ids: 'educational_institution',
  payer_name: 'payer_name',
  open_payments_company: 'open_payments',
  provider_ids: 'providers',
};

// How the execution summary names each param before its id, e.g. "code_set: 12", "cohort 734", "provider ID 99".
const QUERY_PLAN_PARAM_PHRASES: Record<string, string> = {
  code_set: 'code[ _]?sets?',
  filter_set: 'filter[ _]?sets?',
  site_filter_set: '(?:site[ _])?filter[ _]?sets?',
  site_code_set: '(?:site[ _])?code[ _]?sets?',
  cohort_id: '(?:provider[ _])?cohorts?(?:[ _]?ids?)?',
  list_ids: '(?:provider[ _])?lists?(?:[ _]?ids?)?',
  institution_ids: '(?:educational[ _])?institutions?(?:[ _]?ids?)?',
  payer_name: 'payers?(?:[ _]names?)?',
  open_payments_company: 'open[ _]payments?(?:[ _]compan(?:y|ies))?',
  provider_ids: 'providers?(?:[ _]?ids?)?',
};

// Ids shorter than this are only replaced after their param phrase, so "limit 10" or "step 1" stay untouched.
const MIN_BARE_ID_LENGTH = 4;

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Turns an LLM-written execution summary step into a criteria template for `HighlightedCriteria`,
 * replacing the ids it mentions with placeholders backed by the query plan's `ui_metadata`.
 * Every plan step is searched, since the LLM isn't guaranteed to keep one summary step per plan step.
 * Ids written after their param phrase are resolved first; a bare id is only resolved when it belongs to a
 * single entity type. Ids without metadata, ambiguous bare ids, or ids not in the text are left as written.
 */
export function buildExecutionStepCriteria(
  stepText: string,
  planSteps?: MedScout.QueryPlanStep[]
): { criteriaTemplate: string; criteriaInputs: Record<string, unknown> } {
  let criteriaTemplate = stepText ?? '';
  const criteriaInputs: Record<string, unknown> = {};

  // Every id the plan resolved, with the param it came from
  const references: {
    paramKey: string;
    inputType: string;
    rawId: unknown;
    id: string;
    item: Record<string, unknown>;
  }[] = [];
  (planSteps ?? []).forEach((planStep) =>
    Object.entries(planStep?.ui_metadata ?? {}).forEach(([paramKey, metadata]) => {
      const inputType = QUERY_PLAN_PARAM_INPUT_TYPES[paramKey];
      const rawValue = planStep?.params?.[paramKey];
      if (!inputType || !Array.isArray(metadata) || metadata.length === 0 || rawValue == null) {
        return;
      }

      const rawIds = Array.isArray(rawValue) ? rawValue : [rawValue];
      rawIds.forEach((rawId) => {
        const id = String(rawId ?? '');
        const item = metadata.find(
          (meta) => String(meta?.id ?? meta?.provider_id ?? '') === id
        );
        if (id && item) {
          references.push({ paramKey, inputType, rawId, id, item });
        }
      });
    })
  );

  const replaceWithPlaceholder = (
    pattern: RegExp,
    { paramKey, inputType, rawId, item }: (typeof references)[number]
  ) => {
    criteriaTemplate = criteriaTemplate.replace(pattern, () => {
      const componentId = `${paramKey}_${Object.keys(criteriaInputs).length}`;
      criteriaInputs[componentId] = { type: inputType, value: rawId, ui_metadata: [item] };
      return `{{ ${componentId} }}`;
    });
  };
  const quotedIdPattern = (id: string) => `['"]?${escapeRegExp(id)}['"]?(?![\\w-])`;

  // Pass 1: ids written after their param phrase ("filter_set: 12") resolve to that param's entity.
  references.forEach((reference) => {
    replaceWithPlaceholder(
      new RegExp(
        `\\b${QUERY_PLAN_PARAM_PHRASES[reference.paramKey]}\\s*[:#=]?\\s*${quotedIdPattern(reference.id)}`,
        'gi'
      ),
      reference
    );
  });

  // Pass 2: remaining bare ids, only when long enough and the id belongs to a single entity type.
  const referencesById = new Map<string, typeof references>();
  references.forEach((reference) => {
    referencesById.set(reference.id, [...(referencesById.get(reference.id) ?? []), reference]);
  });
  referencesById.forEach((idReferences, id) => {
    const inputTypes = new Set(idReferences.map((reference) => reference.inputType));
    if (id.length < MIN_BARE_ID_LENGTH || inputTypes.size > 1) return;
    replaceWithPlaceholder(new RegExp(`(?<![\\w-])${quotedIdPattern(id)}`, 'g'), idReferences[0]);
  });

  return { criteriaTemplate, criteriaInputs };
}
